import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiTrash2, FiMinus, FiPlus, FiShoppingBag, FiArrowRight, FiTruck } from 'react-icons/fi';
import { useCart } from '../hooks/useCart';
import { useAuthContext } from '../hooks/useAuthContext';
import { useLanguage } from '../context/LanguageContext';
import { supabase } from '../config/supabase';
import { slugify } from '../utils/slugify';
import {
  getCartItemLineTotal,
  getCartItemOriginalUnitPrice,
  getCartItemUnitPrice,
  getCartSubtotal,
  parseDiscountPercent,
} from '../utils/cartPricing';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px 20px;
  min-height: calc(100vh - 200px);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  padding-bottom: 16px;
  border-bottom: 2px solid #e5e7eb;
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #1f2937;
`;

const ItemCount = styled.span`
  font-size: 16px;
  color: #6b7280;
  font-weight: 500;
`;

const Content = styled.div`
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 32px;
  align-items: start;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const CartItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CartItem = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;
  display: grid;
  grid-template-columns: 120px 1fr auto;
  gap: 20px;
  align-items: center;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  }

  @media (max-width: 640px) {
    grid-template-columns: 80px 1fr;
    gap: 12px;
  }
`;

const ItemImage = styled.img`
  width: 120px;
  height: 120px;
  object-fit: cover;
  border-radius: 8px;
  background: #f9fafb;

  @media (max-width: 640px) {
    width: 80px;
    height: 80px;
  }
`;

const ItemDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ItemBrand = styled(Link)`
  font-size: 12px;
  color: #6b7280;
  text-decoration: none;
  font-weight: 500;

  &:hover {
    color: #5efba9;
  }
`;

const ItemTitle = styled(Link)`
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  text-decoration: none;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  &:hover {
    color: #5efba9;
  }
`;

const ItemPrice = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PriceBlock = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
`;

const OriginalPrice = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #9ca3af;
  text-decoration: line-through;
`;

const CurrentUnitPrice = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: #5efba9;
`;

const PriceMultiplier = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
`;

const DiscountNote = styled.span`
  font-size: 12px;
  color: #ef4444;
  font-weight: 600;
`;

const ItemLineTotal = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
  text-align: right;
  white-space: nowrap;
`;

const ItemStock = styled.div`
  font-size: 12px;
  color: ${props => props.inStock ? '#059669' : '#dc2626'};
  font-weight: 500;
`;

const VariantOptions = styled.div`
  margin-top: 8px;
  font-size: 12px;
  color: #6b7280;
`;

const VariantOptionTag = styled.span`
  display: inline-block;
  padding: 4px 8px;
  margin-right: 8px;
  margin-top: 4px;
  background: #f3f4f6;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  color: #374151;
`;

const ItemActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 16px;

  @media (max-width: 640px) {
    grid-column: 1 / -1;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`;

const QuantityControl = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
`;

const QuantityButton = styled.button`
  width: 36px;
  height: 36px;
  border: none;
  background: #f9fafb;
  color: #374151;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: #f3f4f6;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const QuantityDisplay = styled.span`
  padding: 0 16px;
  font-weight: 600;
  color: #1f2937;
  min-width: 40px;
  text-align: center;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #dc2626;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;
  transition: color 0.2s;

  &:hover {
    color: #991b1b;
  }

  svg {
    font-size: 18px;
  }
`;

const Summary = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 24px;
  position: sticky;
  top: 100px;
`;

const SummaryTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 20px;
  color: #1f2937;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  font-size: ${props => props.large ? '18px' : '16px'};
  color: ${props => props.bold ? '#1f2937' : '#6b7280'};
  font-weight: ${props => props.bold ? '700' : '400'};
  border-top: ${props => props.divider ? '2px solid #e5e7eb' : 'none'};
  margin-top: ${props => props.divider ? '12px' : '0'};
  padding-top: ${props => props.divider ? '20px' : '12px'};
`;

const CheckoutButton = styled.button`
  width: 100%;
  padding: 16px;
  background: #5efba9;
  color: #374151;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 20px;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: #4ce89a;
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }

  svg {
    font-size: 20px;
  }
`;

const ContinueShoppingButton = styled(Link)`
  width: 100%;
  padding: 16px;
  background: #5efba9;
  color: #374151;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 12px;
  transition: background 0.2s;

  &:hover {
    background: #4ce89a;
  }
`;

const EmptyCart = styled.div`
  text-align: center;
  padding: 80px 20px;
  grid-column: 1 / -1;
`;

const EmptyCartIcon = styled.div`
  font-size: 80px;
  color: #d1d5db;
  margin-bottom: 24px;
`;

const EmptyCartTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 12px;
`;

const EmptyCartText = styled.p`
  font-size: 16px;
  color: #6b7280;
  margin-bottom: 32px;
`;

const ShopNowButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 32px;
  background: #5efba9;
  color: white;
  border-radius: 8px;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.2s;

  &:hover {
    background: #4ce89a;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 80px 20px;
  color: #6b7280;
  font-size: 18px;
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 24px;
  text-align: center;
`;

function Cart() {
  const navigate = useNavigate();
  const { session } = useAuthContext();
  const { isArabic } = useLanguage();
  const { cartItems, loading, error, cartCount, removeFromCart, updateQuantity, clearCart } = useCart();
  const [variantDetails, setVariantDetails] = useState({}); // { variantId: { price, options: [...] } }
  const [shippingFee, setShippingFee] = useState(0);
  const [shippingRate, setShippingRate] = useState(null);
  const [calculatingShipping, setCalculatingShipping] = useState(true);

  // Fetch default shipping rate and calculate shipping
  useEffect(() => {
    const fetchShippingRate = async () => {
      try {
        setCalculatingShipping(true);
        console.log('🚚 Fetching default shipping rate for cart...');

        // Fetch default shipping rate from view
        const { data: defaultRate, error: rateError } = await supabase
          .from('shipping_rates')
          .select('*')
          .single();

        console.log('Default shipping rate:', defaultRate, 'error:', rateError);

        if (rateError || !defaultRate) {
          console.warn('No default shipping rate found, using 0');
          setShippingRate(null);
          setShippingFee(0);
          setCalculatingShipping(false);
          return;
        }

        setShippingRate(defaultRate);

        // Calculate total cart weight
        let totalWeight = 0;

        // Fetch products to get their weights
        if (cartItems && cartItems.length > 0) {
          const productIds = cartItems.map(item => item.product_id);
          const { data: products, error: productsError } = await supabase
            .from('products')
            .select('id, weight_kg')
            .in('id', productIds);

          console.log('Products with weights:', products);

          if (!productsError && products) {
            cartItems.forEach(item => {
              const product = products.find(p => p.id === item.product_id);
              const weight = parseFloat(product?.weight_kg || 1.000);
              const quantity = parseInt(item.quantity || 1);
              totalWeight += weight * quantity;
            });
          }
        }

        console.log('Total cart weight:', totalWeight, 'kg');

        // Calculate shipping fee using the formula
        const basePrice = parseFloat(defaultRate.base_price || 0);
        const baseWeight = parseFloat(defaultRate.base_weight_kg || 1.000);
        const extraPerKg = parseFloat(defaultRate.extra_per_kg || 0);

        let calculatedShipping = 0;
        if (totalWeight <= baseWeight) {
          calculatedShipping = basePrice;
        } else {
          const extraWeight = totalWeight - baseWeight;
          calculatedShipping = basePrice + (Math.ceil(extraWeight) * extraPerKg);
        }

        console.log('Shipping calculation:', {
          totalWeight,
          basePrice,
          baseWeight,
          extraPerKg,
          calculatedShipping
        });

        setShippingFee(calculatedShipping);
      } catch (error) {
        console.error('Error fetching shipping rate:', error);
        setShippingFee(0);
      } finally {
        setCalculatingShipping(false);
      }
    };

    if (cartItems && cartItems.length > 0) {
      fetchShippingRate();
    } else {
      setShippingFee(0);
      setCalculatingShipping(false);
    }
  }, [cartItems]);

  // Fetch variant option details for cart items with variant_id
  useEffect(() => {
    const fetchVariantDetails = async () => {
      const itemsWithVariants = cartItems.filter(item => item.variant_id);
      if (itemsWithVariants.length === 0) {
        setVariantDetails({});
        return;
      }

      const variantIds = [...new Set(itemsWithVariants.map(item => item.variant_id))];

      try {
        const { data: variantsData, error: variantsError } = await supabase
          .from('product_variants')
          .select(`
            id,
            price,
            variant_options:product_variant_options(
              option_group:product_option_groups(
                id,
                label,
                name
              ),
              option_value:product_option_values(
                id,
                value
              )
            )
          `)
          .in('id', variantIds);

        if (variantsError) {
          console.error('Error fetching variant details:', variantsError);
          return;
        }

        // Build a map of variant_id to option strings
        const detailsMap = {};
        (variantsData || []).forEach(variant => {
          detailsMap[variant.id] = {
            price: variant.price != null ? Number(variant.price) : null,
            options: (variant.variant_options || []).map(vo => ({
              groupLabel: vo.option_group?.label || vo.option_group?.name || 'Option',
              value: vo.option_value?.value || 'Unknown'
            })),
          };
        });

        setVariantDetails(detailsMap);
      } catch (err) {
        console.error('Error fetching variant details:', err);
      }
    };

    if (cartItems.length > 0) {
      fetchVariantDetails();
    }
  }, [cartItems]);

  const handleRemoveItem = async (itemId) => {
    try {
      await removeFromCart(itemId);
    } catch (err) {
      console.error('Error removing item:', err);
      alert('Failed to remove item. Please try again.');
    }
  };

  const handleUpdateQuantity = async (itemId, newQuantity) => {
    try {
      await updateQuantity(itemId, newQuantity);
    } catch (err) {
      console.error('Error updating quantity:', err);
      alert('Failed to update quantity. Please try again.');
    }
  };

  const handleClearCart = async () => {
    if (!window.confirm('Are you sure you want to clear your cart?')) {
      return;
    }
    try {
      await clearCart();
    } catch (err) {
      console.error('Error clearing cart:', err);
      alert('Failed to clear cart. Please try again.');
    }
  };

  const handleCheckout = () => {
    if (!session) {
      alert('Please log in to proceed to checkout');
      navigate('/login');
      return;
    }
    navigate('/checkout');
  };

  // Show loading state
  if (loading) {
    return (
      <Container>
        <LoadingMessage>{isArabic ? 'جاري تحميل سلة التسوق...' : 'Loading your cart...'}</LoadingMessage>
      </Container>
    );
  }

  // Show login prompt if not logged in
  if (!session) {
    return (
      <Container>
        <EmptyCart>
          <EmptyCartIcon>🔒</EmptyCartIcon>
          <EmptyCartTitle>{isArabic ? 'يرجى تسجيل الدخول' : 'Please Log In'}</EmptyCartTitle>
          <EmptyCartText>{isArabic ? 'تحتاج إلى تسجيل الدخول لعرض سلة التسوق الخاصة بك.' : 'You need to be logged in to view your cart.'}</EmptyCartText>
          <ShopNowButton to="/login">
            {isArabic ? 'تسجيل الدخول' : 'Log In'} <FiArrowRight />
          </ShopNowButton>
        </EmptyCart>
      </Container>
    );
  }

  // Show empty cart state
  if (cartItems.length === 0) {
    return (
      <Container>
        <EmptyCart>
          <EmptyCartIcon>
            <FiShoppingBag />
          </EmptyCartIcon>
          <EmptyCartTitle>{isArabic ? 'سلة التسوق فارغة' : 'Your Cart is Empty'}</EmptyCartTitle>
          <EmptyCartText>
            {isArabic ? 'يبدو أنك لم تضف أي شيء إلى سلة التسوق بعد.' : 'Looks like you haven\'t added anything to your cart yet.'}
          </EmptyCartText>
          <ShopNowButton to="/">
            {isArabic ? 'ابدأ التسوق' : 'Start Shopping'} <FiArrowRight />
          </ShopNowButton>
        </EmptyCart>
      </Container>
    );
  }

  const subtotal = getCartSubtotal(cartItems);
  const total = subtotal;

  return (
    <Container>
      <Header>
        <div>
          <Title>{isArabic ? 'سلة التسوق' : 'Shopping Cart'}</Title>
          <ItemCount>{cartCount} {cartCount === 1 ? (isArabic ? 'منتج' : 'item') : (isArabic ? 'منتجات' : 'items')}</ItemCount>
        </div>
        {cartItems.length > 0 && (
          <RemoveButton onClick={handleClearCart} style={{ color: '#6b7280' }}>
            {isArabic ? 'مسح السلة' : 'Clear Cart'}
          </RemoveButton>
        )}
      </Header>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <Content>
        <CartItems>
          {cartItems.map(item => {
            const product = item.product;
            const inStock = product?.stock > 0;
            const stock = product?.stock || 0;
            const currency = product?.currency || 'OMR';
            const unitPrice = getCartItemUnitPrice(item);
            const lineTotal = getCartItemLineTotal(item);
            const variantPrice = item.variant?.price ?? variantDetails[item.variant_id]?.price ?? null;
            const originalUnitPrice = getCartItemOriginalUnitPrice(item, {
              variantPrice,
              productPrice: product?.price,
            });
            const discountPercent = parseDiscountPercent(product?.dis);
            const showOriginalPrice = discountPercent && originalUnitPrice > unitPrice;
            return (
              <CartItem key={item.id}>
                <ItemImage
                  src={product?.images?.[0] || 'https://via.placeholder.com/120'}
                  alt={product?.name || 'Product'}
                />

                <ItemDetails>
                  {product?.brand?.name && (
                    <ItemBrand to={`/${slugify(product.brand.name)}`}>
                      {product.brand.name}
                    </ItemBrand>
                  )}
                  <ItemTitle to={`/product/${product?.id}`}>
                    {product?.name || 'Unknown Product'}
                  </ItemTitle>
                  {item.variant_id && variantDetails[item.variant_id]?.options?.length > 0 && (
                    <VariantOptions>
                      {variantDetails[item.variant_id].options.map((option, idx) => (
                        <VariantOptionTag key={idx}>
                          <strong>{option.groupLabel}:</strong> {option.value}
                        </VariantOptionTag>
                      ))}
                    </VariantOptions>
                  )}
                  <ItemPrice>
                    <PriceBlock dir="ltr">
                      {showOriginalPrice && (
                        <OriginalPrice>{originalUnitPrice.toFixed(2)} {currency}</OriginalPrice>
                      )}
                      <CurrentUnitPrice>{unitPrice.toFixed(2)} {currency}</CurrentUnitPrice>
                      <PriceMultiplier>× {item.quantity}</PriceMultiplier>
                    </PriceBlock>
                    {discountPercent && (
                      <DiscountNote>
                        (-{discountPercent}% {isArabic ? 'خصم مطبّق' : 'discount applied'})
                      </DiscountNote>
                    )}
                  </ItemPrice>
                  <ItemStock inStock={inStock}>
                    {inStock ? `${product.stock} in stock` : 'Out of stock'}
                  </ItemStock>
                  {item.note && (
                    <div style={{ 
                      marginTop: '8px', 
                      padding: '8px 12px', 
                      background: '#f0fdf4', 
                      borderRadius: '6px', 
                      border: '1px solid #d1fae5',
                      fontSize: '13px',
                      color: '#065f46',
                      fontStyle: 'italic'
                    }}>
                      <strong>{isArabic ? 'ملاحظة' : 'Note'}:</strong> {item.note}
                    </div>
                  )}
                </ItemDetails>

                <ItemActions>
                  <ItemLineTotal dir="ltr">
                    {lineTotal.toFixed(2)} {currency}
                  </ItemLineTotal>
                  <QuantityControl>
                    <QuantityButton
                      onClick={() =>
                        handleUpdateQuantity(item.id, item.quantity - 1)
                      }
                      disabled={item.quantity <= 1}
                    >
                      <FiMinus />
                    </QuantityButton>
                    <QuantityDisplay>{item.quantity}</QuantityDisplay>
                    <QuantityButton
                      onClick={() => {
                        if (stock >= item.quantity + 1)
                          handleUpdateQuantity(item.id, item.quantity + 1)
                      }}
                      disabled={!inStock || item.quantity >= product.stock}
                    >
                      <FiPlus />
                    </QuantityButton>
                  </QuantityControl>

                  <RemoveButton onClick={() => handleRemoveItem(item.id)}>
                    <FiTrash2 /> {isArabic ? 'إزالة' : 'Remove'}
                  </RemoveButton>
                </ItemActions>
              </CartItem>
            );
          })}
        </CartItems>

        <Summary>
          <SummaryTitle>{isArabic ? 'ملخص الطلب' : 'Order Summary'}</SummaryTitle>

          <SummaryRow>
            <span>{isArabic ? 'المجموع الفرعي' : 'Subtotal'} ({cartCount} {cartCount === 1 ? (isArabic ? 'منتج' : 'item') : (isArabic ? 'منتجات' : 'items')})</span>
            <span dir="ltr">{subtotal.toFixed(2)} OMR</span>
          </SummaryRow>

          <SummaryRow divider bold large>
            <span>{isArabic ? 'المجموع' : 'Total'}</span>
            <span dir="ltr">{total.toFixed(2)} OMR</span>
          </SummaryRow>

          <CheckoutButton onClick={handleCheckout} disabled={cartItems.length === 0}>
            {isArabic ? 'متابعة إلى الدفع' : 'Proceed to Checkout'} <FiArrowRight />
          </CheckoutButton>

          <ContinueShoppingButton to="/">
            {isArabic ? 'متابعة التسوق' : 'Continue Shopping'}
          </ContinueShoppingButton>
        </Summary>
      </Content>
    </Container>
  );
}

export default Cart;

